import React, { useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";
import EmployeeTooltip from "./EmployeeTooltip";
import { getEmployeeNames } from "./employeeListUtils";

const tenureIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const LEGEND_LABELS = {
  "0_0_6_months": "0-6 мес.",
  "0_6_1_year": "6 мес. - 1 год",
  "1_3_years": "1-3 года",
  "3_5_years": "3-5 лет",
  "5_10_years": "5-10 лет",
  "10_and_more": "10 и более",
};

function TenureSection({ categories, departments }) {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("all");

  const selectedDepartment = useMemo(
    () =>
      selectedDepartmentId === "all"
        ? null
        : departments.find((dept) => String(dept.id) === selectedDepartmentId) || null,
    [departments, selectedDepartmentId],
  );

  const chartData = useMemo(() => {
    const breakdown = selectedDepartment
      ? selectedDepartment.breakdown
      : departments.reduce((acc, dept) => {
          Object.entries(dept.breakdown || {}).forEach(([key, count]) => {
            acc[key] = (acc[key] || 0) + count;
          });
          return acc;
        }, {});

    const total = selectedDepartment
      ? selectedDepartment.total || 0
      : departments.reduce((sum, dept) => sum + (dept.total || 0), 0);

    const items = categories.map((category) => {
      const count = breakdown[category.key] || 0;
      const percent = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        key: category.key,
        color: category.color,
        label: LEGEND_LABELS[category.key] || category.label,
        count,
        percent,
      };
    });

    const donutSegments = items.filter((item) => item.count > 0);
    const gradient = donutSegments.length
      ? `conic-gradient(${donutSegments
          .map((item, index) => {
            const prevShare = donutSegments
              .slice(0, index)
              .reduce((sum, current) => sum + current.count, 0);
            const currentShare = prevShare + item.count;
            const from = (prevShare / total) * 100;
            const to = (currentShare / total) * 100;
            return `${item.color} ${from}% ${to}%`;
          })
          .join(", ")})`
      : "conic-gradient(#e5e7eb 0 100%)";

    return {
      total,
      items,
      gradient,
    };
  }, [categories, departments, selectedDepartment]);

  const employeesByTenureKey = useMemo(() => {
    if (selectedDepartment) {
      const map = {};
      categories.forEach((c) => {
        map[c.key] = selectedDepartment.tenureEmployees?.[c.key] ?? [];
      });
      return map;
    }

    const aggregated = {};
    categories.forEach((c) => {
      aggregated[c.key] = [];
    });

    departments.forEach((dept) => {
      categories.forEach((c) => {
        const employees = dept.tenureEmployees?.[c.key] ?? [];
        if (employees.length) aggregated[c.key].push(...employees);
      });
    });

    return aggregated;
  }, [categories, departments, selectedDepartment]);

  const allVisibleEmployees = useMemo(() => {
    if (selectedDepartment) {
      return selectedDepartment.employees ?? [];
    }
    return departments.flatMap((dept) => dept.employees ?? []);
  }, [departments, selectedDepartment]);

  const scopeLabel = selectedDepartment ? selectedDepartment.name : "Компания";

  if (!departments.length) {
    return (
      <section className="hr-dashboard__card hr-dashboard__card--half">
        <SectionHeader
          icon={tenureIcon}
          iconTone="green"
          title="Стаж работы по отделениям"
          subtitle="Структура по срокам работы"
        />
        <p className="hr-dashboard__empty-state">Нет данных по стажу</p>
      </section>
    );
  }

  return (
    <section className="hr-dashboard__card hr-dashboard__card--half hr-dashboard__card--tenure">
      <SectionHeader
        icon={tenureIcon}
        iconTone="green"
        title="Стаж работы по отделениям"
        subtitle="Структура по срокам работы"
      />

      <label className="hr-dashboard__tenure-filter">
        <select
          value={selectedDepartmentId}
          onChange={(event) => setSelectedDepartmentId(event.target.value)}
          className="hr-dashboard__tenure-select"
          aria-label="Выбор отделения для графика стажа"
        >
          <option value="all">Все отделения</option>
          {departments.map((dept) => (
            <option key={dept.id ?? dept.code} value={String(dept.id)}>
              {dept.name || dept.code}
            </option>
          ))}
        </select>
      </label>

      <div className="hr-dashboard__tenure-layout">
        <div className="hr-dashboard__tenure-donut-wrap">
          <EmployeeTooltip title={scopeLabel} employees={allVisibleEmployees}>
            <div
              className="hr-dashboard__tenure-donut"
              style={{ backgroundImage: chartData.gradient }}
            >
              <div className="hr-dashboard__tenure-donut-center">
                <span className="hr-dashboard__tenure-donut-total-label">Всего</span>
                <span className="hr-dashboard__tenure-donut-total">{chartData.total}</span>
              </div>
            </div>
          </EmployeeTooltip>
        </div>

        <div className="hr-dashboard__tenure-legend">
          {chartData.items.map((item) => (
            <div key={item.key} className="hr-dashboard__tenure-legend-row">
              <div className="hr-dashboard__tenure-legend-label-wrap">
                <span
                  className="hr-dashboard__tenure-legend-dot"
                  style={{ backgroundColor: item.color }}
                />
                <span className="hr-dashboard__tenure-legend-label">{item.label}</span>
              </div>
              <EmployeeTooltip
                title={`${scopeLabel} • ${item.label}`}
                employees={employeesByTenureKey[item.key]}
                emptyText={
                  item.count > 0 && !getEmployeeNames(employeesByTenureKey[item.key]).length
                    ? "Список сотрудников недоступен"
                    : "Нет сотрудников в этой категории"
                }
              >
                <div className="hr-dashboard__tenure-legend-values">
                  <div className="hr-dashboard__tenure-legend-track">
                    <div
                      className="hr-dashboard__tenure-legend-fill"
                      style={{
                        width: `${item.percent}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                  <span className="hr-dashboard__tenure-legend-count">{item.count}</span>
                  <span className="hr-dashboard__tenure-legend-percent">{item.percent}%</span>
                </div>
              </EmployeeTooltip>
            </div>
          ))}
          {!chartData.items.length && (
            <p className="hr-dashboard__empty-state">Нет данных по выбранному отделению</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default TenureSection;
